import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Plus, DollarSign, CheckCircle, Clock, MoreVertical, Eye, Edit,
  Printer, Download, Mail, CreditCard, AlertTriangle, XCircle,
  Trash2, Search, Loader2
} from 'lucide-react';
import { CreateInvoiceModal } from '../components/CreateInvoiceModal';
import { EmailComposerModal } from '../components/EmailComposerModal';
import { useNavigate } from 'react-router-dom';

export const InvoicesPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [leadSales, setLeadSales] = useState([]);
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [menuDirection, setMenuDirection] = useState('down');
  const [editInvoice, setEditInvoice] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get('/api/invoices'),
      api.get('/api/clients'),
      api.get('/api/billing/products'),
      api.get('/api/billing/services'),
      api.get('/api/billing/lead-sales').catch(() => ({ data: { lead_sales: [] } })),
    ]).then(([inv, cli, prod, svc, ls]) => {
      setInvoices(inv.data || []);
      setClients((cli.data?.clients || cli.data || []).map(c => ({ ...c, name: c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() })));
      setProducts(prod.data?.products || []);
      setServices(svc.data?.services || []);
      setLeadSales(ls.data?.lead_sales || []);
    }).catch(() => toast.error('Failed to load data'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!openMenu) return;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(null); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openMenu]);

  const loadInvoices = () => {
    api.get('/api/invoices').then(res => setInvoices(res.data || []));
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setShowClientPicker(false);
    setEditInvoice(null);
    setModalOpen(true);
  };

  const handleChargeNow = (inv) => {
    var baseAmt = inv.total || 0;
    var ccRate = 0.029;
    var ccFlat = 0.30;
    var chargeAmt = Math.round(((baseAmt + ccFlat) / (1 - ccRate)) * 100) / 100;
    var feeAmt = Math.round((chargeAmt - baseAmt) * 100) / 100;
    if (!window.confirm(
      'Invoice: ' + (inv.invoice_number || '') +
      '\nClient: ' + (inv.client_name || '') +
      '\n\nInvoice Total: $' + baseAmt.toFixed(2) +
      '\nProcessing Fee (2.9% + $0.30): $' + feeAmt.toFixed(2) +
      '\nTotal Charge: $' + chargeAmt.toFixed(2) +
      '\n\nCharge $' + chargeAmt.toFixed(2) + ' to the client\'s card on file?'
    )) return;
    api.post('/api/invoices/' + inv.id + '/charge-now').then(function(res) {
      toast.success(res.data.message || 'Charged successfully');
      loadInvoices();
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Charge failed');
    });
  };

  const handleSendInvoice = (inv) => {
    api.post('/api/invoices/' + inv.id + '/send').then(function(res) {
      toast.success(res.data.message || 'Invoice sent');
      loadInvoices();
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    });
  };

  const handleMarkPaid = (inv) => {
    api.post('/api/invoices/' + inv.id + '/mark-paid').then(function() {
      toast.success('Invoice marked as paid');
      loadInvoices();
    }).catch(function() { toast.error('Failed'); });
  };

  const handleMarkOverdue = (inv) => {
    api.post('/api/invoices/' + inv.id + '/mark-overdue').then(function() {
      toast.success('Invoice marked as overdue');
      loadInvoices();
    }).catch(function() { toast.error('Failed'); });
  };

  const handleCancel = (inv) => {
    if (!window.confirm('Cancel invoice ' + (inv.invoice_number || '') + '?')) return;
    api.post('/api/invoices/' + inv.id + '/cancel').then(function() {
      toast.success('Invoice cancelled');
      loadInvoices();
    }).catch(function() { toast.error('Failed'); });
  };

  const handleDelete = (inv) => {
    if (!window.confirm('Delete invoice ' + (inv.invoice_number || '') + '? This cannot be undone.')) return;
    api.delete('/api/invoices/' + inv.id).then(function() {
      toast.success('Invoice deleted');
      loadInvoices();
    }).catch(function() { toast.error('Failed to delete'); });
  };

  const handleEditInvoice = (inv) => {
    const client = clients.find(c => c.id === inv.client_id);
    setSelectedClient(client || { id: inv.client_id, name: inv.client_name, email: inv.client_email || '' });
    setEditInvoice(inv);
    setModalOpen(true);
  };

  const handleSendEmail = (inv) => {
    const client = clients.find(c => c.id === inv.client_id);
    setEmailTarget({ email: client?.email || inv.client_email || '', name: inv.client_name || '', clientId: inv.client_id });
    setShowEmailModal(true);
  };

  // Filter and search
  const filtered = invoices.filter(i => {
    if (filter !== 'all' && i.status !== filter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const searchable = [i.invoice_number, i.client_name, i.client_email, ...(i.items || []).map(it => it.name)].filter(Boolean).join(' ').toLowerCase();
      if (!searchable.includes(q)) return false;
    }
    return true;
  });

  const totalAmt = invoices.reduce((s, i) => s + (i.total || 0), 0);
  const paidAmt = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0);
  const unpaidAmt = invoices.filter(i => i.status !== 'paid' && i.status !== 'cancelled').reduce((s, i) => s + (i.total || 0), 0);

  const formatCurrency = (a) => '$' + (a || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }) : '-';

  const statusBadge = (status) => {
    const styles = {
      paid: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      sent: 'bg-blue-100 text-blue-700',
      draft: 'bg-gray-100 text-gray-600',
      overdue: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-200 text-gray-500',
    };
    return styles[status] || 'bg-gray-100 text-gray-600';
  };

  const statusIcon = (status) => {
    if (status === 'paid') return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (status === 'overdue') return <AlertTriangle className="w-4 h-4 text-red-500" />;
    if (status === 'cancelled') return <XCircle className="w-4 h-4 text-gray-400" />;
    return <Clock className="w-4 h-4 text-yellow-500" />;
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1628]">Invoices</h1>
          <p className="text-gray-500">Manage billing and payments</p>
        </div>
        <Button onClick={() => setShowClientPicker(true)} className="bg-[#014DB7] text-white rounded-xl" data-testid="create-invoice-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Invoice
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-5 border">
          <DollarSign className="w-6 h-6 text-[#014DB7] mb-2" />
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-xl font-bold">{formatCurrency(totalAmt)}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border">
          <CheckCircle className="w-6 h-6 text-green-500 mb-2" />
          <p className="text-sm text-gray-500">Paid</p>
          <p className="text-xl font-bold text-green-600">{formatCurrency(paidAmt)}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border">
          <Clock className="w-6 h-6 text-yellow-500 mb-2" />
          <p className="text-sm text-gray-500">Unpaid</p>
          <p className="text-xl font-bold text-yellow-600">{formatCurrency(unpaidAmt)}</p>
        </div>
      </div>

      {/* Filters + Search */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex gap-2">
          {['all', 'pending', 'sent', 'paid', 'overdue', 'cancelled'].map(s => (
            <button key={s} onClick={() => setFilter(s)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${filter === s ? 'bg-[#014DB7] text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'}`} data-testid={`filter-${s}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)} {s === 'all' ? `(${invoices.length})` : `(${invoices.filter(i => i.status === s).length})`}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search invoices..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="invoice-search" />
        </div>
      </div>

      {/* Invoice Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <DollarSign className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No invoices found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Invoice #</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Client</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">For</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Due Date</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Items</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map(inv => {
                const itemNames = (inv.items || []).map(it => it.name || it.description).filter(Boolean).join(', ');
                const itemCount = (inv.items || []).length;
                const clientObj = clients.find(c => c.id === inv.client_id);
                const companyName = clientObj?.company_name || '';

                return (
                  <tr key={inv.id} className="hover:bg-gray-50" data-testid={`invoice-row-${inv.id}`}>
                    <td className="px-4 py-4">
                      <span className="font-mono font-semibold text-[#014DB7]">{inv.invoice_number}</span>
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        {companyName && <span className="text-xs font-semibold text-orange-600 uppercase mr-2">{companyName}</span>}
                        <span className="text-blue-700 font-medium">{inv.client_name || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-gray-600 max-w-[200px] truncate" title={itemNames}>{itemNames || '-'}</td>
                    <td className="px-4 py-4 text-gray-500">{formatDate(inv.created_at)}</td>
                    <td className="px-4 py-4">
                      <span className={`text-sm ${inv.status !== 'paid' && inv.due_date && new Date(inv.due_date) < new Date() ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                        {formatDate(inv.due_date)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center text-gray-500">{itemCount} items</td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-bold text-gray-900">{formatCurrency(inv.total)}</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center gap-1">
                        {statusIcon(inv.status)}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusBadge(inv.status)}`}>
                          {inv.status || 'pending'}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => window.open('/crm/invoice/' + inv.id + '/print', '_blank')}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          title="Print"
                          data-testid={`print-invoice-${inv.id}`}
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                        <div className="relative" ref={openMenu === inv.id ? menuRef : null}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Calculate if menu should open upward
                              const rect = e.currentTarget.getBoundingClientRect();
                              const spaceBelow = window.innerHeight - rect.bottom;
                              setMenuDirection(spaceBelow < 350 ? 'up' : 'down');
                              setOpenMenu(openMenu === inv.id ? null : inv.id);
                            }}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                            data-testid={`invoice-menu-${inv.id}`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {openMenu === inv.id && (
                            <div className={`absolute right-0 bg-white border rounded-xl shadow-xl z-50 w-52 py-1 ${menuDirection === 'up' ? 'bottom-8' : 'top-8'}`} data-testid={`invoice-dropdown-${inv.id}`}>
                              {/* View / Edit / Print / Send */}
                              <button onClick={() => { setOpenMenu(null); navigate('/crm/invoice/' + inv.id + '/print'); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                                <Eye className="w-4 h-4" /> View Invoice
                              </button>
                              <button onClick={() => { setOpenMenu(null); handleEditInvoice(inv); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                                <Edit className="w-4 h-4" /> Edit Invoice
                              </button>
                              <button onClick={() => { setOpenMenu(null); const w = window.open('/crm/invoice/' + inv.id + '/print', '_blank'); if (w) setTimeout(() => w.print(), 1500); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                                <Download className="w-4 h-4" /> Print / Download
                              </button>
                              <button onClick={() => { setOpenMenu(null); handleSendInvoice(inv); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                                <Mail className="w-4 h-4" /> Send to Client
                              </button>

                              {/* Divider */}
                              <div className="border-t my-1" />

                              {/* Payment actions — only for unpaid */}
                              {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                                <>
                                  <button onClick={() => { setOpenMenu(null); handleChargeNow(inv); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 flex items-center gap-3 text-blue-600 font-medium">
                                    <CreditCard className="w-4 h-4" /> Pay Now
                                  </button>
                                  <button onClick={() => { setOpenMenu(null); handleMarkPaid(inv); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-green-50 flex items-center gap-3 text-green-600 font-medium">
                                    <CheckCircle className="w-4 h-4" /> Mark as Paid
                                  </button>
                                  <button onClick={() => { setOpenMenu(null); handleMarkOverdue(inv); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-orange-50 flex items-center gap-3 text-orange-600 font-medium">
                                    <AlertTriangle className="w-4 h-4" /> Mark as Overdue
                                  </button>
                                  <button onClick={() => { setOpenMenu(null); handleCancel(inv); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 text-gray-600">
                                    <XCircle className="w-4 h-4" /> Cancel Invoice
                                  </button>
                                </>
                              )}

                              {/* Divider */}
                              <div className="border-t my-1" />

                              <button onClick={() => { setOpenMenu(null); handleDelete(inv); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-red-50 flex items-center gap-3 text-red-600 font-medium">
                                <Trash2 className="w-4 h-4" /> Delete Invoice
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Client Picker Modal */}
      {showClientPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowClientPicker(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Select Client</h3>
            <div className="space-y-2">
              {clients.map(c => (
                <button key={c.id} onClick={() => handleClientSelect(c)} className="w-full text-left p-3 rounded-lg border hover:bg-gray-50 hover:border-[#014DB7] transition-colors">
                  <p className="font-medium text-[#0A1628]">{c.company_name && <span className="text-orange-600 text-xs uppercase mr-2">{c.company_name}</span>}{c.name}</p>
                  <p className="text-sm text-gray-500">{c.email}</p>
                </button>
              ))}
              {clients.length === 0 && <p className="text-gray-500 text-center py-4">No clients found</p>}
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Invoice Modal */}
      {modalOpen && selectedClient && (
        <CreateInvoiceModal
          client={selectedClient}
          products={products}
          services={services}
          leadSales={leadSales}
          locations={[]}
          editInvoice={editInvoice}
          onSave={(data) => {
            const endpoint = editInvoice ? '/api/invoices/' + editInvoice.id : '/api/invoices';
            const method = editInvoice ? api.put : api.post;
            method(endpoint, data).then(() => {
              toast.success(editInvoice ? 'Invoice updated' : 'Invoice created');
              setModalOpen(false);
              setSelectedClient(null);
              setEditInvoice(null);
              loadInvoices();
            }).catch(() => toast.error('Failed'));
          }}
          onClose={() => { setModalOpen(false); setSelectedClient(null); setEditInvoice(null); }}
        />
      )}

      {/* Email Composer */}
      <EmailComposerModal
        isOpen={showEmailModal}
        onClose={() => { setShowEmailModal(false); setEmailTarget(null); }}
        recipientEmail={emailTarget?.email}
        recipientName={emailTarget?.name}
        clientId={emailTarget?.clientId}
      />
    </div>
  );
};

export default InvoicesPage;
